import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import type {
  MsmeAuthFeatureColorScheme,
  MsmeAuthFeatureIcon,
} from "../../../content/msmeAuthMarketing";

export const MSME_FEATURE_COLOR_THEMES: Record<
  MsmeAuthFeatureColorScheme,
  { bg: string; border: string; iconColor: string }
> = {
  blue: { bg: "#eff6ff", border: "#dbeafe", iconColor: "#2563eb" },
  orange: { bg: "#fff7ed", border: "#ffedd5", iconColor: "#f97316" },
  green: { bg: "#f0fdf4", border: "#dcfce7", iconColor: "#16a34a" },
  purple: { bg: "#faf5ff", border: "#f3e8ff", iconColor: "#9333ea" },
};

export const MSME_FEATURE_GRID_THEMES: Record<
  MsmeAuthFeatureColorScheme,
  {
    bg: string;
    border: string;
    iconColor: string;
    glow: string;
    accent: string;
  }
> = {
  blue: {
    bg: "linear-gradient(160deg, #eff6ff 0%, #f8fbff 55%, #ffffff 100%)",
    border: "#bfdbfe",
    iconColor: "#2563eb",
    glow: "rgba(37, 99, 235, 0.14)",
    accent: "#1d4ed8",
  },
  orange: {
    bg: "linear-gradient(160deg, #fff7ed 0%, #fffbf5 55%, #ffffff 100%)",
    border: "#fed7aa",
    iconColor: "#ea580c",
    glow: "rgba(234, 88, 12, 0.14)",
    accent: "#c2410c",
  },
  green: {
    bg: "linear-gradient(160deg, #f0fdf4 0%, #f7fef9 55%, #ffffff 100%)",
    border: "#bbf7d0",
    iconColor: "#16a34a",
    glow: "rgba(22, 163, 74, 0.14)",
    accent: "#15803d",
  },
  purple: {
    bg: "linear-gradient(160deg, #faf5ff 0%, #fdfaff 55%, #ffffff 100%)",
    border: "#e9d5ff",
    iconColor: "#9333ea",
    glow: "rgba(147, 51, 234, 0.14)",
    accent: "#7e22ce",
  },
};

const FEATURE_ICON_MAP: Record<MsmeAuthFeatureIcon, typeof HubOutlinedIcon> = {
  onboarding: PersonAddOutlinedIcon,
  catalogue: Inventory2OutlinedIcon,
  account: ManageAccountsOutlinedIcon,
  inventory: Inventory2OutlinedIcon,
  logistics: LocalShippingOutlinedIcon,
  visibility: VisibilityOutlinedIcon,
};

export function renderMsmeFeatureIcon(
  iconKey: MsmeAuthFeatureIcon,
  color: string,
  size = 16
) {
  const Icon = FEATURE_ICON_MAP[iconKey] ?? HubOutlinedIcon;
  return <Icon sx={{ fontSize: size, color }} />;
}

export const msmeFeatureIconBoxSx = {
  width: 24,
  height: 24,
  borderRadius: "6px",
  bgcolor: "#ffffff",
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  mt: "1px",
} as const;

export const msmeGridFeatureIconBoxSx = {
  width: 40,
  height: 40,
  borderRadius: "10px",
  bgcolor: "#ffffff",
  boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
} as const;

export const msmeFeatureTitleSx = {
  fontWeight: 500,
  fontSize: "clamp(12px, 1.3vw, 13px)",
  mb: 0.5,
  lineHeight: "150%",
  letterSpacing: 0,
} as const;

export const msmeGridFeatureTitleSx = {
  fontWeight: 700,
  fontSize: "clamp(12px, 1.25vw, 14px)",
  lineHeight: 1.35,
  letterSpacing: "-0.01em",
  color: "#0f172a",
} as const;

export const msmeFeatureDescriptionSx = {
  fontWeight: 500,
  fontSize: "clamp(11px, 1.15vw, 12px)",
  lineHeight: "150%",
  letterSpacing: 0,
  color: "rgba(0, 0, 0, 0.5)",
} as const;

export const msmeGridFeatureDescriptionSx = {
  fontWeight: 400,
  fontSize: "clamp(11px, 1.1vw, 12px)",
  lineHeight: 1.5,
  color: "#64748b",
} as const;
